import { type UniqueIdentifier } from "@dnd-kit/core";
import { type UseSortableArguments, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { type CSSProperties, type HTMLAttributes } from "react";
import type { TreeItem, TreeItemComponentType } from "./types";

export interface TreeItemProps<T> extends Omit<HTMLAttributes<HTMLElement>, "id"> {
  clone?: boolean;
  depth: number;
  disableAnimation?: boolean;
  disableInteraction?: boolean;
  disableSelection?: boolean;
  ghost?: boolean;
  handleProps?: any;
  indicator?: boolean;
  indentationWidth: number;
  item: TreeItem<T>;

  wrapperRef?(node: HTMLLIElement): void;
}

type SortableTreeItemProps<T, TElement extends HTMLElement> = TreeItemProps<T> & {
  id: UniqueIdentifier;
  isActive: boolean;
  isExpanded: boolean;
  isHighlighted: boolean;
  TreeItemComponent: TreeItemComponentType<T, TElement>;
  isDisabled?: boolean;
  sortableProps?: Omit<UseSortableArguments, "id">;
  toggleExpanded: () => void;
};

export const SortableTreeItem = <T, TElement extends HTMLElement>({
  id,
  depth,
  TreeItemComponent,
  disableAnimation,
  isDisabled,
  ...props
}: SortableTreeItemProps<T, TElement>) => {
  const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    id,
    disabled: isDisabled,
    animateLayoutChanges: ({ isSorting, wasDragging }) =>
      !!disableAnimation || isSorting || wasDragging ? false : true
  });

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition
  };

  return (
    <TreeItemComponent
      {...props}
      setRef={setNodeRef}
      style={style}
      depth={depth}
      isDragging={isDragging}
      handleProps={{
        ...attributes,
        ...listeners
      }}
    />
  );
};
